/* eslint-disable camelcase */
import cx from 'classnames';
import {
  FormEvent, InputHTMLAttributes, useEffect, useRef, useState,
} from 'react';
import { Address } from '../features/serviceLocation/serviceLocationSlice';
import styles from '../sass/components/SearchLocationInput.module.scss';
import { LatLngBoundsLiteral } from '../util/placesBias';
import {
  leadingHouseNumber,
  resolveNumberedAddress,
  withPostalCode,
} from '../util/placesLookup';
import usePlacesAutocomplete from '../hooks/usePlacesAutocomplete';
import TextInput from './TextInput';
import i18n from '../i18n';

export type PlaceResult = google.maps.places.PlaceResult;

/**
 * Shown when the visitor submits or leaves text Google has no address for.
 * The booking is driven by the picked place (postal code, coordinates), so
 * free text can never be "good enough".
 */
export const NO_MATCH_MESSAGE = 'Pick your address from the list so we know'
  + ' exactly where the car is. We serve addresses in Canada and the US.';

/**
 * A numbered suggestion Google could only place on the street: the number
 * is kept in the box and the visitor is asked to pick again, never moved to
 * a street with no number (QA 2026-09-25 #24).
 */
export const houseNumberMessage = (number: string, street: string): string => (
  i18n.t('location.errors.houseNumber', { number, street })
);

/**
 * How long after the box loses focus before we decide nothing was picked. A
 * mouse pick commits through Google's own listeners, which can land a beat
 * after the blur; this gives them that beat.
 */
export const BLUR_SETTLE_MS = 250;

export function placeResultToAddress(
  newAddr: PlaceResult | undefined,
): Address | undefined {
  if (!newAddr) {
    return undefined;
  }

  const streetNumber = newAddr?.address_components?.find(
    (component) => component.types.includes('street_number'),
  )?.long_name;
  const street = newAddr?.address_components?.find(
    (component) => component.types.includes('route'),
  )?.long_name;
  const city = newAddr?.address_components?.find(
    (component) => component.types.includes('locality'),
  )?.long_name || '';
  const postalCode = newAddr?.address_components?.find(
    (component) => component.types.includes('postal_code'),
  )?.long_name || '';
  const country = newAddr?.address_components?.find(
    (component) => component.types.includes('country'),
  )?.long_name || '';
  const province = newAddr?.address_components?.find(
    (component) => component.types.includes('administrative_area_level_1'),
  )?.long_name || '';
  const countryCode = newAddr?.address_components?.find(
    (component) => component.types.includes('country'),
  )?.short_name || '';
  const provinceCode = newAddr?.address_components?.find(
    (component) => component.types.includes('administrative_area_level_1'),
  )?.short_name || '';
  const lat = newAddr?.geometry?.location?.lat();
  const lng = newAddr?.geometry?.location?.lng();
  return {
    // Never "undefined Fifth St": a place with no number is the street alone.
    street: [streetNumber, street].filter(Boolean).join(' '),
    city,
    postalCode,
    province,
    country,
    countryCode,
    provinceCode,
    lat,
    lng,
  };
}

/**
 * What Google's suggestion list (`.pac-container`, appended to <body>) is
 * doing right now. Google hides it with an inline `display: none`, and marks
 * the highlighted row `.pac-item-selected`.
 */
export function suggestionListState(
  doc: Document = document,
): 'closed' | 'open' | 'highlighted' {
  const containers = Array.from(
    doc.querySelectorAll<HTMLElement>('.pac-container'),
  );
  const open = containers.filter((c) => {
    if (!c.querySelector('.pac-item')) {
      return false;
    }
    const view = doc.defaultView;
    const display = view
      ? view.getComputedStyle(c).display
      : c.style.display;
    return display !== 'none';
  });
  if (!open.length) {
    return 'closed';
  }
  return open.some((c) => c.querySelector('.pac-item-selected'))
    ? 'highlighted'
    : 'open';
}

/**
 * A keydown Google's widget reads as the down arrow. It checks the legacy
 * `keyCode`, which the constructor accepts in some browsers and not others,
 * so the property is pinned on the instance as well.
 */
export function arrowDownEvent(): KeyboardEvent {
  const event = new KeyboardEvent('keydown', {
    key: 'ArrowDown',
    code: 'ArrowDown',
    bubbles: true,
    cancelable: true,
  });
  ['keyCode', 'which'].forEach((prop) => {
    try {
      Object.defineProperty(event, prop, { get: () => 40 });
    } catch (e) {
      // read-only in this engine: the `key` alone will have to do
    }
  });
  return event;
}

interface SearchLocationProps {
  onChange: (value: PlaceResult | undefined) => void;
  value?: PlaceResult | undefined;
  title: string;
  id: string;
  errorMessage?: string;
  className?: string;
  success?: boolean;
  disabled?: boolean;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  name?: string;
  /**
   * Text to seed the box with on first render when there is no selected
   * place yet - used for the marketing-site handoff, where the visitor
   * already typed an address and only has to confirm it here.
   */
  initialValue?: string;
  /**
   * Area to rank suggestions towards (not restrict to): the visitor's own
   * city, when we know it. See util/placesBias.
   */
  bounds?: LatLngBoundsLiteral | null;
  /**
   * Language for the Maps script, e.g. `fr` for a visitor handed over from
   * the French marketing site. Only the first load of the script on the page
   * can set it; a script already on the page is reused as it is.
   */
  language?: string | null;
}

function Input({
  onChange,
  value,
  title,
  id,
  errorMessage,
  className,
  success,
  disabled,
  autoComplete,
  name,
  initialValue,
  bounds,
  language,
}: SearchLocationProps) {
  const [inputValue, setInputValue] = useState(
    value?.formatted_address || initialValue || '',
  );
  // Text in the box that Google had no address for.
  const [noMatch, setNoMatch] = useState(false);
  // A numbered pick Google could not confirm (see houseNumberMessage).
  const [numberMessage, setNumberMessage] = useState('');
  // Latest selected place, readable from listeners that outlive a render.
  const selected = useRef(value);
  selected.current = value;
  const typed = useRef(inputValue);
  typed.current = inputValue;

  const commitPlace = (place: PlaceResult) => {
    setNoMatch(false);
    setNumberMessage('');
    onChange(place);
    setInputValue(place.formatted_address || '');
  };

  const handleSelectPlace = (selectedPlace: PlaceResult | undefined) => {
    if (!selectedPlace) {
      onChange(undefined);
      return;
    }
    if (!selectedPlace.address_components) return;
    const hasNumber = selectedPlace.address_components
      .some((c) => c.types.includes('street_number'));
    const typedNumber = leadingHouseNumber(typed.current);
    if (hasNumber && !selectedPlace.address_components
      .some((c) => c.types.includes('postal_code'))) {
      // A real doorstep with no postal code from Google: look it up.
      withPostalCode(selectedPlace).then(commitPlace);
      return;
    }
    if (hasNumber || !typedNumber) {
      commitPlace(selectedPlace);
      return;
    }
    // "555 5th Street" picked, the street came back without its number:
    // look the numbered address up, keeping what they typed meanwhile.
    const typedText = typed.current;
    const street = selectedPlace.address_components
      .find((c) => c.types.includes('route'))?.long_name || '';
    resolveNumberedAddress(typedNumber, selectedPlace).then((numbered) => {
      if (numbered) {
        commitPlace(numbered);
        return;
      }
      setInputValue(typedText);
      setNoMatch(false);
      setNumberMessage(houseNumberMessage(typedNumber, street || typedText));
    });
  };

  // Maps loads when the visitor comes to the box, not when it mounts
  // (QA 2026-09-25 #14; hooks/usePlacesAutocomplete).
  const { ref } = usePlacesAutocomplete<HTMLInputElement>({
    onPlaceSelected: handleSelectPlace,
    language: language || undefined,
    listLabel: i18n.t('location.suggestions'),
    // Google sets the box to "off"; put back what the caller asked for
    // (e.g. "street-address", QA 2026-09-25 #29), or the old default.
    ...(autoComplete && autoComplete !== 'off'
      && { inputAutocompleteValue: autoComplete }),
    options: {
      types: ['address'],
      fields: ['place_id',
        'formatted_address', 'address_components', 'geometry'],
      componentRestrictions: { country: ['us', 'ca'] },
      ...(bounds && { bounds, strictBounds: false }),
    },
  });

  /** True when the box holds text that is not the picked place. */
  const holdsUnpickedText = () => {
    const text = typed.current.trim();
    return !!text && text !== (selected.current?.formatted_address || '');
  };

  // Enter with the list open and nothing highlighted used to do nothing at
  // all. Google commits the HIGHLIGHTED row on Enter, so highlight the first
  // one for it (a synthetic ArrowDown) and let the same Enter go through.
  // Capture phase on the input itself, so this runs before Google's own
  // listener sees the Enter. Enter with nothing to pick tells the visitor so.
  useEffect(() => {
    const input = ref.current;
    if (!input) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.isComposing) {
        return;
      }
      const state = suggestionListState(input.ownerDocument);
      if (state === 'open') {
        input.dispatchEvent(arrowDownEvent());
      } else if (state === 'closed' && holdsUnpickedText()) {
        setNoMatch(true);
      }
    };
    input.addEventListener('keydown', onKeyDown, true);
    return () => input.removeEventListener('keydown', onKeyDown, true);
    // ref.current is stable for the life of the input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textInputChange = (event: FormEvent<HTMLInputElement>) => {
    setNoMatch(false);
    setNumberMessage('');
    setInputValue(event.currentTarget.value);
  };

  // Leaving the box: snap back to the picked place if there is one (as
  // before); otherwise keep what they typed and say it has to be picked.
  const blurTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(blurTimer.current), []);
  const handleBlur = () => {
    window.clearTimeout(blurTimer.current);
    blurTimer.current = window.setTimeout(() => {
      if (selected.current?.formatted_address) {
        setInputValue(selected.current.formatted_address);
        return;
      }
      if (!selected.current && holdsUnpickedText()) {
        setNoMatch(true);
      }
    }, BLUR_SETTLE_MS);
  };

  return (
    <TextInput
      onChange={textInputChange}
      onBlur={handleBlur}
      value={inputValue}
      title={title}
      id={id}
      ref={ref}
      errorMessage={numberMessage || errorMessage
        || (noMatch ? i18n.t('location.errors.noMatch') : undefined)}
      className={className}
      success={success}
      disabled={disabled}
      autoComplete={autoComplete}
      name={name}
    />
  );
}

export default function SearchLocationInput({
  onChange,
  value,
  name,
  title,
  id,
  errorMessage,
  className,
  success,
  disabled,
  autoComplete,
  initialValue,
  bounds,
  language,
}: SearchLocationProps) {
  return (
    <Input
      onChange={onChange}
      value={value}
      title={title}
      id={id}
      errorMessage={errorMessage}
      success={success}
      className={cx(styles.locationInput, className)}
      disabled={disabled}
      autoComplete={autoComplete}
      name={name}
      initialValue={initialValue}
      bounds={bounds}
      language={language}
    />
  );
}
